// ================================
// CHROMA 3D PRINTING - SCRIPTS
// ================================

// Debounce function for performance
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', function() {

    // === HERO CANVAS ANIMATION - 3D PRINTING PROCESS ===
    // Inspired by Mimaki 3DUJ-2207 full-color UV inkjet 3D printing
    const canvas = document.getElementById('heroCanvas');

    if (canvas) {
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let scrollY = 0;

        // Brand colors
        const colors = {
            purple: { r: 123, g: 44, b: 191 },
            purpleLight: { r: 157, g: 78, b: 221 },
            cyan: { r: 6, g: 182, b: 212 },
            cyanLight: { r: 34, g: 211, b: 238 },
            pink: { r: 255, g: 0, b: 110 },
            pinkLight: { r: 255, g: 45, b: 135 },
            white: { r: 255, g: 255, b: 255 },
            dark: { r: 15, g: 15, b: 30 }
        };

        // Animation state
        const state = {
            time: 0,
            cycleTime: 0,
            cycleDuration: 600, // ~10 seconds at 60fps
            layers: [],
            particles: [],
            printHead: { x: 0, y: 0, active: false, direction: 1 },
            currentLayer: 0,
            totalLayers: 24,
            buildPlatformY: 0,
            objectWidth: 0,
            objectHeight: 0,
            objectX: 0
        };

        // Layer class for building effect
        class Layer {
            constructor(y, index, totalLayers) {
                this.y = y;
                this.index = index;
                this.height = 8;
                this.opacity = 0;
                this.solidified = false;
                this.glowIntensity = 0;
                this.printed = false;

                // Color mixing - gradient across the layer
                const colorIndex = index % 3;
                const colorArray = [colors.purple, colors.cyan, colors.pink];
                this.primaryColor = colorArray[colorIndex];
                this.secondaryColor = colorArray[(colorIndex + 1) % 3];

                // Support material for certain layers
                this.hasSupport = index % 5 === 0 && index > 0 && index < totalLayers - 2;
            }

            print(progress) {
                this.printed = true;
                this.opacity = Math.min(0.3 + progress * 0.4, 0.7);
                this.glowIntensity = 1 - progress;
            }

            solidify(progress) {
                this.solidified = true;
                this.opacity = 0.7 + progress * 0.3;
                this.glowIntensity = Math.max(0, this.glowIntensity - 0.02);
            }

            draw(ctx, objectX, objectWidth) {
                if (!this.printed) return;

                const x = objectX;
                const width = objectWidth;

                // Draw support material first (translucent white)
                if (this.hasSupport) {
                    ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity * 0.15})`;
                    ctx.fillRect(x - 20, this.y, 15, this.height);
                    ctx.fillRect(x + width + 5, this.y, 15, this.height);
                }

                // Create gradient for color mixing effect
                const gradient = ctx.createLinearGradient(x, this.y, x + width, this.y);
                const { r: r1, g: g1, b: b1 } = this.primaryColor;
                const { r: r2, g: g2, b: b2 } = this.secondaryColor;

                gradient.addColorStop(0, `rgba(${r1}, ${g1}, ${b1}, ${this.opacity})`);
                gradient.addColorStop(0.5, `rgba(${(r1+r2)/2}, ${(g1+g2)/2}, ${(b1+b2)/2}, ${this.opacity})`);
                gradient.addColorStop(1, `rgba(${r2}, ${g2}, ${b2}, ${this.opacity})`);

                ctx.fillStyle = gradient;
                ctx.fillRect(x, this.y, width, this.height);

                // Glow effect on freshly deposited material
                if (this.glowIntensity > 0) {
                    ctx.shadowColor = `rgba(${r1}, ${g1}, ${b1}, ${this.glowIntensity * 0.8})`;
                    ctx.shadowBlur = 15 * this.glowIntensity;
                    ctx.fillStyle = `rgba(255, 255, 255, ${this.glowIntensity * 0.3})`;
                    ctx.fillRect(x, this.y, width, this.height);
                    ctx.shadowBlur = 0;
                }
            }
        }

        // Particle class for print head effect
        class Particle {
            constructor(x, y, color) {
                this.x = x;
                this.y = y;
                this.vx = (Math.random() - 0.5) * 2;
                this.vy = Math.random() * 2 + 1;
                this.life = 1;
                this.decay = Math.random() * 0.03 + 0.02;
                this.size = Math.random() * 3 + 1;
                this.color = color;
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;
                this.vy += 0.1; // gravity
                this.life -= this.decay;
            }

            draw(ctx) {
                if (this.life <= 0) return;
                const { r, g, b } = this.color;
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${this.life * 0.8})`;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size * this.life, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Initialize layers
        function initLayers() {
            state.layers = [];
            state.currentLayer = 0;
            state.cycleTime = 0;

            const centerX = canvas.width * 0.65;
            const centerY = canvas.height * 0.55;

            state.objectWidth = Math.min(200, canvas.width * 0.15);
            state.objectHeight = state.totalLayers * 8;
            state.objectX = centerX - state.objectWidth / 2;
            state.buildPlatformY = centerY + state.objectHeight / 2 + 20;

            for (let i = 0; i < state.totalLayers; i++) {
                const y = state.buildPlatformY - (i + 1) * 8;
                state.layers.push(new Layer(y, i, state.totalLayers));
            }

            state.printHead = {
                x: state.objectX - 50,
                y: state.layers[0].y,
                active: false,
                direction: 1,
                targetX: state.objectX + state.objectWidth + 50
            };
        }

        // Draw build platform / grid
        function drawBuildPlatform() {
            const platformY = state.buildPlatformY;
            const platformWidth = state.objectWidth + 100;
            const platformX = state.objectX - 50;

            // Grid lines
            ctx.strokeStyle = 'rgba(123, 44, 191, 0.08)';
            ctx.lineWidth = 0.5;

            const gridSize = 20;
            const gridExtent = Math.max(canvas.width, canvas.height);

            // Vertical lines
            for (let x = 0; x < gridExtent; x += gridSize) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }

            // Horizontal lines
            for (let y = 0; y < gridExtent; y += gridSize) {
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            // Build platform surface
            ctx.fillStyle = 'rgba(123, 44, 191, 0.1)';
            ctx.fillRect(platformX, platformY, platformWidth, 4);

            // Platform glow
            const platformGradient = ctx.createLinearGradient(platformX, platformY, platformX, platformY + 30);
            platformGradient.addColorStop(0, 'rgba(123, 44, 191, 0.15)');
            platformGradient.addColorStop(1, 'rgba(123, 44, 191, 0)');
            ctx.fillStyle = platformGradient;
            ctx.fillRect(platformX, platformY, platformWidth, 30);
        }

        // Draw print head
        function drawPrintHead() {
            const head = state.printHead;
            if (!head.active) return;

            const headWidth = 60;
            const headHeight = 20;

            // Print head body
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fillRect(head.x - headWidth/2, head.y - headHeight - 15, headWidth, headHeight);

            // Nozzle array visualization
            ctx.fillStyle = 'rgba(6, 182, 212, 0.8)';
            for (let i = 0; i < 5; i++) {
                ctx.fillRect(head.x - 20 + i * 10, head.y - 15, 6, 8);
            }

            // UV light effect (curing)
            ctx.fillStyle = 'rgba(157, 78, 221, 0.3)';
            ctx.beginPath();
            ctx.moveTo(head.x - 25, head.y - 5);
            ctx.lineTo(head.x + 25, head.y - 5);
            ctx.lineTo(head.x + 35, head.y + 5);
            ctx.lineTo(head.x - 35, head.y + 5);
            ctx.closePath();
            ctx.fill();

            // Emit particles
            if (Math.random() > 0.5) {
                const colorArray = [colors.purple, colors.cyan, colors.pink];
                const particleColor = colorArray[Math.floor(Math.random() * 3)];
                state.particles.push(new Particle(
                    head.x + (Math.random() - 0.5) * 40,
                    head.y - 5,
                    particleColor
                ));
            }
        }

        // Update animation state
        function update() {
            state.time++;
            state.cycleTime++;

            // Calculate current layer based on cycle time
            const layerDuration = state.cycleDuration / state.totalLayers;
            const currentLayerIndex = Math.min(
                Math.floor(state.cycleTime / layerDuration),
                state.totalLayers - 1
            );

            // Update print head position
            if (currentLayerIndex < state.totalLayers) {
                const layer = state.layers[currentLayerIndex];
                const layerProgress = (state.cycleTime % layerDuration) / layerDuration;

                state.printHead.active = true;
                state.printHead.y = layer.y;

                // Move print head across layer
                const startX = state.objectX - 50;
                const endX = state.objectX + state.objectWidth + 50;
                const headProgress = layerProgress;

                if (state.printHead.direction === 1) {
                    state.printHead.x = startX + (endX - startX) * headProgress;
                } else {
                    state.printHead.x = endX - (endX - startX) * headProgress;
                }

                // Print current layer
                if (layerProgress > 0.1 && layerProgress < 0.9) {
                    layer.print(layerProgress);
                }

                // Switch direction for next layer
                if (layerProgress > 0.95 && currentLayerIndex !== state.currentLayer) {
                    state.printHead.direction *= -1;
                    state.currentLayer = currentLayerIndex;
                }
            } else {
                state.printHead.active = false;
            }

            // Solidify previous layers
            state.layers.forEach((layer, idx) => {
                if (layer.printed && idx < currentLayerIndex - 1) {
                    const solidifyProgress = Math.min((currentLayerIndex - idx) / 5, 1);
                    layer.solidify(solidifyProgress);
                }
            });

            // Update particles
            state.particles.forEach(p => p.update());
            state.particles = state.particles.filter(p => p.life > 0);

            // Reset cycle
            if (state.cycleTime >= state.cycleDuration + 120) { // Add pause at end
                initLayers();
            }
        }

        // Main render function
        function render() {
            // Clear with dark background
            ctx.fillStyle = `rgb(${colors.dark.r}, ${colors.dark.g}, ${colors.dark.b})`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Apply parallax offset based on scroll
            ctx.save();
            ctx.translate(0, scrollY * 0.1);

            // Draw build platform and grid
            drawBuildPlatform();

            // Draw all layers
            state.layers.forEach(layer => {
                layer.draw(ctx, state.objectX, state.objectWidth);
            });

            // Draw particles
            state.particles.forEach(p => p.draw(ctx));

            // Draw print head
            drawPrintHead();

            ctx.restore();
        }

        // Animation loop
        function animate() {
            update();
            render();
            animationFrameId = requestAnimationFrame(animate);
        }

        // Handle resize
        function resizeCanvas() {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
            initLayers();
        }

        // Handle scroll for parallax
        function handleScroll() {
            scrollY = window.pageYOffset;
        }

        // Initialize
        resizeCanvas();
        window.addEventListener('resize', debounce(resizeCanvas, 250));
        window.addEventListener('scroll', handleScroll, { passive: true });

        animate();

        // Pause when tab not visible
        document.addEventListener('visibilitychange', function() {
            if (document.hidden) {
                cancelAnimationFrame(animationFrameId);
            } else {
                animate();
            }
        });
    }

    // === SHOWCASE FLOATING PARTICLES ===
    const showcaseParticles = document.getElementById('showcaseParticles');

    if (showcaseParticles) {
        const particleColorClasses = ['purple', 'cyan', 'pink'];

        function createFloatingParticle() {
            const particle = document.createElement('div');

            // Random color class from brand colors
            const colorClass = particleColorClasses[Math.floor(Math.random() * particleColorClasses.length)];
            particle.className = 'floating-particle ' + colorClass;

            // Random size between 5px and 10px
            const size = Math.random() * 5 + 5;
            particle.style.width = size + 'px';
            particle.style.height = size + 'px';

            // Random position anywhere in the container
            particle.style.left = Math.random() * 100 + '%';
            particle.style.top = Math.random() * 100 + '%';

            // Random fade duration (6-12 seconds)
            const duration = Math.random() * 6 + 6;
            particle.style.animationDuration = duration + 's';

            showcaseParticles.appendChild(particle);

            // Remove and recreate after animation
            setTimeout(() => particle.remove(), duration * 1000);
        }

        // Create initial particles scattered around
        for (let i = 0; i < 15; i++) {
            setTimeout(() => createFloatingParticle(), i * 200);
        }

        // Continuously create new particles
        setInterval(createFloatingParticle, 600);
    }

    // === SHOWCASE CAROUSEL WITH PRINTER ANIMATION ===
    const showcaseCarousel = document.getElementById('showcaseCarousel');

    if (showcaseCarousel) {
        const slides = showcaseCarousel.querySelectorAll('.carousel-slide');
        const prevBtn = showcaseCarousel.querySelector('.carousel-prev');
        const nextBtn = showcaseCarousel.querySelector('.carousel-next');

        let currentIndex = 0;
        const totalSlides = slides.length;
        const autoplayInterval = 6000; // 6 seconds
        let autoplayTimer = null;
        let isCarouselVisible = false;

        // Printing animation state for each slide
        const slideAnimations = [];

        // Brand colors for particles
        const particleColors = ['#7B2CBF', '#06B6D4', '#FF006E', '#9D4EDD'];

        // Initialize each slide's animation state
        slides.forEach((slide, index) => {
            slideAnimations[index] = {
                isPrinting: false,
                animationFrameId: null,
                animationStartTime: null
            };
        });

        // Update slide positions
        function updateSlidePositions() {
            slides.forEach((slide, index) => {
                const position = getSlidePosition(index);
                slide.setAttribute('data-position', position);
            });
        }

        // Get position relative to current
        function getSlidePosition(index) {
            const diff = index - currentIndex;

            if (diff === 0) return 'active';
            if (diff === 1 || (currentIndex === totalSlides - 1 && index === 0)) return 'next';
            if (diff === -1 || (currentIndex === 0 && index === totalSlides - 1)) return 'prev';
            return 'hidden';
        }

        // Navigate to slide
        function goToSlide(index) {
            // Stop animation on current active slide
            stopPrintingAnimation(currentIndex);

            // Update index (wrap around)
            currentIndex = ((index % totalSlides) + totalSlides) % totalSlides;

            // Update positions
            updateSlidePositions();

            // Start animation on new active slide
            if (isCarouselVisible) {
                startPrintingAnimation(currentIndex);
            }

            // Reset autoplay
            resetAutoplay();
        }

        // Next slide
        function nextSlide() {
            goToSlide(currentIndex + 1);
        }

        // Previous slide
        function prevSlide() {
            goToSlide(currentIndex - 1);
        }

        // === PRINTING ANIMATION FUNCTIONS ===

        // Create particle effect
        function createParticle(particlesContainer) {
            const particle = document.createElement('div');
            particle.className = 'particle';
            particle.style.left = Math.random() * 50 + 5 + 'px';
            particle.style.backgroundColor = particleColors[Math.floor(Math.random() * particleColors.length)];
            particlesContainer.appendChild(particle);
            setTimeout(() => particle.remove(), 600);
        }

        // Animate printing for a specific slide
        function animatePrinting(slideIndex, timestamp) {
            const state = slideAnimations[slideIndex];
            if (!state.isPrinting) return;

            const slide = slides[slideIndex];
            const printer = slide.querySelector('.showcase-printer');
            const printerImage = slide.querySelector('.printer-image');
            const printHead = slide.querySelector('.print-head');
            const imageContainer = slide.querySelector('.printer-image-container');
            const particlesContainer = slide.querySelector('.print-head-particles');

            if (!state.animationStartTime) state.animationStartTime = timestamp;

            const elapsed = timestamp - state.animationStartTime;
            const animationDuration = 5000;
            const progress = Math.min(elapsed / animationDuration, 1);

            // Update clip-path to reveal image from bottom to top
            const clipTop = (1 - progress) * 100;
            printerImage.style.clipPath = `inset(${clipTop}% 0 0 0)`;

            // Position print head
            const containerHeight = imageContainer.offsetHeight;
            const headPosition = containerHeight * (1 - progress) - 15;
            printHead.style.top = Math.max(0, headPosition) + 'px';

            // Oscillate print head horizontally
            const oscillation = Math.sin(elapsed / 80) * 120;
            printHead.style.transform = `translateX(${oscillation}px)`;

            // Create particles
            if (Math.random() > 0.7) {
                createParticle(particlesContainer);
            }

            // Continue or complete
            if (progress < 1) {
                state.animationFrameId = requestAnimationFrame((ts) => animatePrinting(slideIndex, ts));
            } else {
                completePrintingAnimation(slideIndex);
            }
        }

        // Start printing animation for a slide
        function startPrintingAnimation(slideIndex) {
            const state = slideAnimations[slideIndex];
            if (state.isPrinting) return;

            const slide = slides[slideIndex];
            const printer = slide.querySelector('.showcase-printer');
            const printerImage = slide.querySelector('.printer-image');
            const printHead = slide.querySelector('.print-head');
            const imageContainer = slide.querySelector('.printer-image-container');

            state.isPrinting = true;
            state.animationStartTime = null;

            // Reset state
            printerImage.style.clipPath = 'inset(100% 0 0 0)';
            printHead.style.top = imageContainer.offsetHeight + 'px';

            printer.classList.remove('complete');
            printer.classList.add('printing');

            state.animationFrameId = requestAnimationFrame((ts) => animatePrinting(slideIndex, ts));
        }

        // Complete printing animation
        function completePrintingAnimation(slideIndex) {
            const state = slideAnimations[slideIndex];
            const slide = slides[slideIndex];
            const printer = slide.querySelector('.showcase-printer');
            const printerImage = slide.querySelector('.printer-image');

            state.isPrinting = false;
            printer.classList.remove('printing');
            printer.classList.add('complete');

            // Hold then restart
            setTimeout(() => {
                // Only restart if this slide is still active
                if (slide.getAttribute('data-position') === 'active' && isCarouselVisible) {
                    printer.classList.remove('complete');
                    printerImage.style.clipPath = 'inset(100% 0 0 0)';
                    startPrintingAnimation(slideIndex);
                }
            }, 2000);
        }

        // Stop printing animation
        function stopPrintingAnimation(slideIndex) {
            const state = slideAnimations[slideIndex];
            if (state.animationFrameId) {
                cancelAnimationFrame(state.animationFrameId);
            }
            state.isPrinting = false;
            state.animationStartTime = null;

            const slide = slides[slideIndex];
            const printer = slide.querySelector('.showcase-printer');
            printer.classList.remove('printing', 'complete');
        }

        // === AUTOPLAY ===
        function startAutoplay() {
            if (autoplayTimer) clearInterval(autoplayTimer);
            autoplayTimer = setInterval(nextSlide, autoplayInterval);
        }

        function stopAutoplay() {
            if (autoplayTimer) {
                clearInterval(autoplayTimer);
                autoplayTimer = null;
            }
        }

        function resetAutoplay() {
            stopAutoplay();
            if (isCarouselVisible) {
                startAutoplay();
            }
        }

        // === EVENT LISTENERS ===
        prevBtn.addEventListener('click', prevSlide);
        nextBtn.addEventListener('click', nextSlide);

        // Click on slide to make it active
        slides.forEach((slide, index) => {
            slide.addEventListener('click', () => {
                if (index !== currentIndex) {
                    goToSlide(index);
                }
            });
        });

        // Pause autoplay on hover
        showcaseCarousel.addEventListener('mouseenter', stopAutoplay);
        showcaseCarousel.addEventListener('mouseleave', () => {
            if (isCarouselVisible) startAutoplay();
        });

        // === INTERSECTION OBSERVER ===
        const carouselObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                isCarouselVisible = entry.isIntersecting;
                if (entry.isIntersecting) {
                    startPrintingAnimation(currentIndex);
                    startAutoplay();
                } else {
                    // Stop all animations when not visible
                    slides.forEach((_, index) => stopPrintingAnimation(index));
                    stopAutoplay();
                }
            });
        }, { threshold: 0.3 });

        carouselObserver.observe(showcaseCarousel);

        // === VISIBILITY CHANGE ===
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                slides.forEach((_, index) => stopPrintingAnimation(index));
                stopAutoplay();
            } else if (isCarouselVisible) {
                startPrintingAnimation(currentIndex);
                startAutoplay();
            }
        });

        // === INITIALIZE ===
        updateSlidePositions();
    }

    // === GALLERY FILTER ===
    const filterBtns = document.querySelectorAll('.filter-btn');
    const galleryItems = document.querySelectorAll('.gallery-item');

    if (filterBtns.length > 0 && galleryItems.length > 0) {
        filterBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                // Update active button
                filterBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');

                // Get filter category - normalize text to match data-category values
                let filter = btn.textContent.trim().toLowerCase();

                // Map button text to data-category values
                if (filter === 'd&d') filter = 'dnd';

                // Filter items
                galleryItems.forEach(item => {
                    const category = item.dataset.category;
                    if (filter === 'all' || category === filter) {
                        item.classList.remove('hidden');
                    } else {
                        item.classList.add('hidden');
                    }
                });
            });
        });
    }

    // === MOBILE MENU TOGGLE ===
    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
    const navLinks = document.querySelector('.nav-links');
    
    if (mobileMenuToggle) {
        mobileMenuToggle.addEventListener('click', function() {
            navLinks.classList.toggle('active');
            this.classList.toggle('active');
        });
    }
    
    // Close mobile menu when clicking a link
    const navLinkItems = document.querySelectorAll('.nav-links a');
    navLinkItems.forEach(link => {
        link.addEventListener('click', function() {
            navLinks.classList.remove('active');
            mobileMenuToggle.classList.remove('active');
        });
    });
    
    // === SMOOTH SCROLLING ===
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
            const href = this.getAttribute('href');
            
            // Ignore empty anchors and # only
            if (href === '#' || href === '') {
                e.preventDefault();
                return;
            }
            
            const target = document.querySelector(href);
            if (target) {
                e.preventDefault();
                const navHeight = document.querySelector('.navbar').offsetHeight;
                const targetPosition = target.offsetTop - navHeight;
                
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            }
        });
    });
    
    // === NAVBAR SCROLL EFFECT ===
    const navbar = document.querySelector('.navbar');
    let lastScroll = 0;

    window.addEventListener('scroll', function() {
        const currentScroll = window.pageYOffset;

        if (currentScroll > 100) {
            navbar.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
        } else {
            navbar.style.boxShadow = 'none';
        }

        lastScroll = currentScroll;
    });

    // === UPLOAD AREA INTERACTION ===
    const uploadArea = document.querySelector('.upload-area');
    
    if (uploadArea) {
        // Prevent default drag behaviors
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            uploadArea.addEventListener(eventName, preventDefaults, false);
            document.body.addEventListener(eventName, preventDefaults, false);
        });
        
        function preventDefaults(e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Highlight drop area when item is dragged over
        ['dragenter', 'dragover'].forEach(eventName => {
            uploadArea.addEventListener(eventName, highlight, false);
        });
        
        ['dragleave', 'drop'].forEach(eventName => {
            uploadArea.addEventListener(eventName, unhighlight, false);
        });
        
        function highlight(e) {
            uploadArea.style.borderColor = 'var(--color-primary)';
            uploadArea.style.background = 'var(--color-text-primary)';
        }

        function unhighlight(e) {
            uploadArea.style.borderColor = 'var(--color-border)';
            uploadArea.style.background = 'var(--color-surface)';
        }
        
        // Handle dropped files
        uploadArea.addEventListener('drop', handleDrop, false);
        
        function handleDrop(e) {
            const dt = e.dataTransfer;
            const files = dt.files;
            
            handleFiles(files);
        }
        
        function handleFiles(files) {
            // This is where you'd handle the file upload
            console.log('Files to upload:', files);
            
            // For now, just show an alert
            if (files.length > 0) {
                alert(`File selected: ${files[0].name}\n\nQuote calculator coming soon!`);
            }
        }
        
        // Handle button click for file selection
        const uploadButton = uploadArea.querySelector('.btn-primary');
        if (uploadButton) {
            uploadButton.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                // Create hidden file input
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = '.stl,.obj,.3mf,.vrml';
                
                fileInput.addEventListener('change', function(e) {
                    handleFiles(this.files);
                });
                
                fileInput.click();
            });
        }
    }
    
    // === INTERSECTION OBSERVER FOR ANIMATIONS ===
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver(function(entries) {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up');
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);
    
    // Observe elements for animation
    const animatedElements = document.querySelectorAll('.step-card, .benefit-card, .gallery-item, .testimonial-card');
    animatedElements.forEach(el => observer.observe(el));
    
    // === TESTIMONIAL ROTATION (Optional Enhancement) ===
    // You can add automatic testimonial rotation here if needed
    
    // === FORM VALIDATION (For future implementation) ===
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
        form.addEventListener('submit', function(e) {
            e.preventDefault();
            // Add your form validation and submission logic here
            console.log('Form submitted');
        });
    });

    // === FAQ ACCORDION ===
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
        const question = item.querySelector('.faq-question');
        if (question) {
            question.addEventListener('click', () => {
                const isActive = item.classList.contains('active');

                // Close all other items
                faqItems.forEach(otherItem => {
                    otherItem.classList.remove('active');
                    otherItem.querySelector('.faq-question')?.setAttribute('aria-expanded', 'false');
                });

                // Toggle current item
                if (!isActive) {
                    item.classList.add('active');
                    question.setAttribute('aria-expanded', 'true');
                }
            });
        }
    });

    // === TESTIMONIALS SLIDER ===
    const testimonialsSlider = document.querySelector('.testimonials-slider');

    if (testimonialsSlider) {
        const grid = testimonialsSlider.querySelector('.testimonials-grid');
        const cards = grid.querySelectorAll('.testimonial-card');
        const prevBtn = testimonialsSlider.querySelector('.slider-arrow-prev');
        const nextBtn = testimonialsSlider.querySelector('.slider-arrow-next');

        // Check if we're on desktop (arrows visible)
        function isDesktop() {
            return window.innerWidth >= 768;
        }

        // Get scroll amount (one card width + gap)
        function getScrollAmount() {
            const card = cards[0];
            const cardWidth = card.offsetWidth;
            const gridStyle = window.getComputedStyle(grid);
            const gap = parseInt(gridStyle.gap) || parseInt(gridStyle.columnGap) || 16;
            return cardWidth + gap;
        }

        // Update button states based on scroll position
        function updateButtonStates() {
            if (!isDesktop()) return;

            const scrollLeft = grid.scrollLeft;
            const maxScroll = grid.scrollWidth - grid.clientWidth;

            prevBtn.disabled = scrollLeft <= 0;
            nextBtn.disabled = scrollLeft >= maxScroll - 5; // 5px tolerance
        }

        // Next slide
        function nextSlide() {
            const scrollAmount = getScrollAmount();
            grid.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }

        // Previous slide
        function prevSlide() {
            const scrollAmount = getScrollAmount();
            grid.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }

        // Event listeners
        prevBtn.addEventListener('click', prevSlide);
        nextBtn.addEventListener('click', nextSlide);

        // Update button states on scroll
        grid.addEventListener('scroll', debounce(updateButtonStates, 50));

        // Handle resize
        window.addEventListener('resize', debounce(() => {
            updateButtonStates();
        }, 250));

        // Initialize
        updateButtonStates();
    }

    // Theme toggle is handled by inline script in snippets/theme-toggle.liquid

    console.log('🎨 Chroma 3D Printing - Website loaded successfully');
});

// === UTILITY FUNCTIONS ===

// Debounce function for performance
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Check if element is in viewport
function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}
